import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

/**
 * Overlay open/close synced with URL search params via React Router (single source of truth).
 * - Open: pushes a history entry (`setSearchParams` without replace).
 * - Close: `navigate(-1)` when this session opened the overlay; otherwise strips the param with replace.
 * - Browser back/forward: React Router updates search params → `isOpen` updates automatically.
 */
export function useModalSearchParamHistory({
  paramName,
  paramValue = "open",
  isActiveInUrl: isActiveInUrlProp,
  onActiveFromUrl,
  onInactive,
  resetKey,
  // ignored — kept so existing call sites do not break
  navigateBack: _navigateBack,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const pushedRef = useRef(false);
  const prevIsOpenRef = useRef(false);
  const onActiveRef = useRef(onActiveFromUrl);
  const onInactiveRef = useRef(onInactive);
  onActiveRef.current = onActiveFromUrl;
  onInactiveRef.current = onInactive;

  const checkActive = useCallback(
    (params) => {
      if (typeof isActiveInUrlProp === "function") {
        const url = new URL(window.location.href);
        const qs = params.toString();
        url.search = qs ? `?${qs}` : "";
        return isActiveInUrlProp(url);
      }
      if (paramValue != null) {
        return params.get(paramName) === paramValue;
      }
      return params.has(paramName);
    },
    [isActiveInUrlProp, paramName, paramValue]
  );

  const isOpen = checkActive(searchParams);

  useEffect(() => {
    pushedRef.current = false;
    prevIsOpenRef.current = false;
  }, [resetKey]);

  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      onActiveRef.current?.();
    } else if (!isOpen && prevIsOpenRef.current) {
      onInactiveRef.current?.();
      pushedRef.current = false;
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  const setParam = useCallback(
    (value, { replace = false } = {}) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(paramName, value ?? paramValue ?? "open");
          return next;
        },
        { replace }
      );
    },
    [paramName, paramValue, setSearchParams]
  );

  const openOverlay = useCallback(
    (value) => {
      if (checkActive(searchParams)) {
        return;
      }

      setParam(value, { replace: false });
      pushedRef.current = true;
    },
    [checkActive, searchParams, setParam]
  );

  const closeOverlay = useCallback(() => {
    if (pushedRef.current) {
      pushedRef.current = false;
      navigate(-1);
      return;
    }
    if (checkActive(searchParams)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(paramName);
          return next;
        },
        { replace: true }
      );
    }
  }, [checkActive, navigate, paramName, searchParams, setSearchParams]);

  const pushParam = useCallback(
    (value) => {
      openOverlay(value);
    },
    [openOverlay]
  );

  const replaceParam = useCallback(
    (value) => {
      setParam(value, { replace: true });
    },
    [setParam]
  );

  const markNavigatePushed = useCallback(() => {
    pushedRef.current = true;
  }, []);

  return {
    isOpen,
    openOverlay,
    closeOverlay,
    pushParam,
    replaceParam,
    historyBackIfParamPresent: closeOverlay,
    markNavigatePushed,
    closeNavigateOverlay: closeOverlay,
  };
}
